import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUnreadCount } from '../../api/notifications';
import { HiMenuAlt2 } from 'react-icons/hi';

export default function Header({ title, breadcrumbs, onMenuToggle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const userMenuRef = useRef(null);

  const initials = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U';
  const canSeeNotifications = user?.role === 'superadmin' || user?.allowed_features?.includes('notifications');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!canSeeNotifications) return;
    getUnreadCount().then(res => setUnreadCount(res.count || 0)).catch(() => {});
    const interval = setInterval(() => {
      getUnreadCount().then(res => setUnreadCount(res.count || 0)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [canSeeNotifications]);

  return (
    <header
      className="bg-white sticky top-0 z-30 flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 lg:px-10 lg:py-5"
      style={{
        borderBottom: '1px solid #E5E5E0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      {/* Left: Hamburger + Title */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <HiMenuAlt2 className="w-6 h-6" />
        </button>
        <div>
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-900">{title}</h2>
          {breadcrumbs && (
            <div className="flex items-center gap-2 text-[12px] sm:text-[13px]" style={{ color: '#999999' }}>
              <Link to="/dashboard" className="no-underline hover:underline" style={{ color: '#043959' }}>Home</Link>
              <span>/</span>
              <span>{breadcrumbs}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right: Search + Actions + User */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Search Box - hidden on mobile */}
        <div className="relative hidden md:block">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 opacity-50 text-sm">🔍</span>
          <input
            type="text"
            placeholder="Search contracts, services..."
            className="text-sm transition-all"
            style={{
              width: '280px',
              padding: '10px 16px 10px 40px',
              border: '1px solid #E5E5E0',
              borderRadius: '8px',
              background: '#FAFAF8',
              fontFamily: "'Lexend', sans-serif",
              fontSize: '14px',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#043959';
              e.target.style.boxShadow = '0 0 0 3px rgba(4,57,89,0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#E5E5E0';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Icon Buttons - hidden on small mobile */}
        <div className="hidden sm:flex gap-2 sm:gap-3">
          {canSeeNotifications && <button
            onClick={() => navigate('/notifications')}
            className="relative flex items-center justify-center text-base transition-all"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #E5E5E0',
              background: 'white',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FAFAF8';
              e.currentTarget.style.borderColor = '#043959';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#E5E5E0';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🔔
            {unreadCount > 0 && (
              <span
                className="absolute flex items-center justify-center rounded-full text-white"
                style={{
                  top: '-6px',
                  right: '-6px',
                  minWidth: '18px',
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 700,
                  background: '#EF4444',
                  border: '2px solid white',
                  padding: '0 3px',
                  fontFamily: "'Lexend', sans-serif",
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>}

          <button
            onClick={() => navigate('/profile')}
            className="flex items-center justify-center text-base transition-all"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              border: '1px solid #E5E5E0',
              background: 'white',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#FAFAF8';
              e.currentTarget.style.borderColor = '#043959';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#E5E5E0';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            ⚙️
          </button>
        </div>

        {/* User Menu with Dropdown */}
        <div className="relative" ref={userMenuRef}>
          <div
            className="flex items-center gap-2 sm:gap-3 rounded-lg cursor-pointer transition-all p-1.5 sm:p-2"
            onClick={() => setShowUserMenu(!showUserMenu)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAF8'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div
              className="flex items-center justify-center text-white font-semibold text-sm"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #043959, #F4A460)',
              }}
            >
              {initials}
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</span>
              <span className="text-xs" style={{ color: '#999999' }}>EC Member</span>
            </div>
          </div>

          {/* Dropdown */}
          {showUserMenu && (
            <div
              className="absolute right-0 bg-white rounded-xl overflow-hidden"
              style={{
                top: 'calc(100% + 8px)',
                minWidth: '200px',
                border: '1px solid #E5E5E0',
                boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                zIndex: 100,
              }}
            >
              <div style={{ padding: '16px', borderBottom: '1px solid #F0F0EB' }}>
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
                <p className="text-xs" style={{ color: '#999999', marginTop: '2px' }}>{user?.email}</p>
              </div>

              <div style={{ padding: '8px 0' }}>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                  className="w-full flex items-center gap-3 text-left text-sm transition-colors bg-transparent border-0 cursor-pointer"
                  style={{
                    padding: '10px 16px',
                    color: '#1A1A1A',
                    fontFamily: "'Lexend', sans-serif",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAF8'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span className="text-base">✏️</span>
                  Edit Profile
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); logout(); }}
                  className="w-full flex items-center gap-3 text-left text-sm transition-colors bg-transparent border-0 cursor-pointer"
                  style={{
                    padding: '10px 16px',
                    color: '#EF4444',
                    fontFamily: "'Lexend', sans-serif",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAF8'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span className="text-base">🚪</span>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
